import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Link } from "@/components/ui/link"
import ArrowLeftIcon from "@/components/icons/arrow-left"
import PayWithBalanceFlow from "./pay-with-balance-flow"
import { Payment, usePaymentPage } from "@/api/payment-pages"
import { Transaction } from "@/api/transactions"
import { useState } from "react"
import PayReceipt from "@/pay/pay-receipt"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { useWallets } from "@/api/wallets"
import { useUser } from "@/api/users"
import WalletUnlock from "@/wallet/wallet-unlock"
import { PaymentLinkType } from "@/api/payment-links"
import ArrowDownIcon from "@/components/icons/arrow-down"
import DownloadReceiptLink from "@/pay/download-receipt.link"

interface PayWithBalanceProps {
  shortId: string;
}

export default function PayWithBalance({ shortId }: PayWithBalanceProps) {
  const [payment, setPayment] = useState<Payment>()
  const [isPaying, setIsPaying] = useState(false)
  const [transaction, setTransaction] = useState<Transaction>()
  const { token, paymentPage, networks, amount, minAmount, maxAmount } =
    usePaymentPage(shortId!)
  const { wallet, isLoading } = useWallets()
  const { user } = useUser()

  const onDone = (payment: Payment, transaction: Transaction) => {
    setPayment(payment)
    setTransaction(transaction)
  }

  if (!user || !paymentPage || isLoading) return null

  if (payment && transaction) {
    return (
      <div className="flex flex-col gap-6 items-center">
        <PayReceipt transaction={transaction} />
        <div className="flex justify-around gap-8">
          <DownloadReceiptLink shortId={shortId} paymentId={payment.id} />
          <a
            href="/"
            className="flex flex-row gap-2 items-center text-blue-500 font-semibold text-sm"
          >
            <ArrowDownIcon direction="right" size={20} />
            Go to dashboard
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-8">
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle>Pay With {token}</CardTitle>
          <CardDescription>
            Use your BraidPay's account balance to make this payment
          </CardDescription>
        </CardHeader>
        <CardContent>
          {paymentPage.user.id === user.id ? (
            <Alert variant="destructive">
              <AlertTitle>Same User</AlertTitle>
              <AlertDescription>
                You cannot pay your own invoices or payment links with your own
                account
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {wallet ? (
                <WalletUnlock>
                  <PayWithBalanceFlow
                    user={user}
                    wallet={wallet}
                    networks={networks}
                    token={token}
                    amount={amount}
                    amountOptions={
                      paymentPage.paymentLink?.type ===
                      PaymentLinkType.CUSTOM_AMOUNT
                        ? {
                            min: minAmount,
                            max: maxAmount,
                            presetAmounts:
                              paymentPage.paymentLink.presetAmounts,
                          }
                        : undefined
                    }
                    shortId={shortId}
                    onDone={onDone}
                    onStart={() => setIsPaying(true)}
                  />
                </WalletUnlock>
              ) : (
                <Alert variant="destructive">
                  <AlertTitle>Wallet not found</AlertTitle>
                  <AlertDescription>
                    You need to have a BraidPay wallet to make a payment using
                    BraidPay
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>
      {!isPaying && (
        <Link
          to={`/${shortId}`}
          className="text-blue-500 font-semibold text-sm"
        >
          <ArrowLeftIcon />
          Go back
        </Link>
      )}
    </div>
  )
}
